<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  paymentConfigService,
  type PaymentProcessor,
} from '@/services/payment-config.services';

const props = defineProps<{ storeId: string }>();

const { t } = useI18n({ useScope: 'global' });
const qc = useQueryClient();

// --- Processors ---
const PROCESSORS = computed<
  { key: PaymentProcessor; label: string; icon: string; desc: string }[]
>(() => [
  {
    key: 'mock',
    label: 'Mock',
    icon: 'pi-code',
    desc: t('payment.descMock'),
  },
  {
    key: 'on_site',
    label: 'On Site',
    icon: 'pi-map-marker',
    desc: t('payment.descOnSite'),
  },
  {
    key: 'payfast',
    label: 'PayFast',
    icon: 'pi-bolt',
    desc: t('payment.descPayFast'),
  },
]);

const selectedProcessor = ref<PaymentProcessor>('mock');
const activeProc = computed(() =>
  PROCESSORS.value.find((p) => p.key === selectedProcessor.value)!,
);

// --- Payment configs for this store ---
const { data: configs, isPending: loading } = useQuery({
  queryKey: computed(() => ['payment-configs', props.storeId]),
  queryFn: () => paymentConfigService.list(props.storeId),
  enabled: computed(() => !!props.storeId),
});

type FormState = {
  enabled: boolean;
  displayName: string;
  shopId: string;
  apiKey: string;
  webhookSecret: string;
  // Optionnels : une clé PayFast appartient au produit sur lequel elle a été
  // créée et un webhook n'entend que son propre produit, donc encaisser sur
  // un TPE (terminal physique) exige sa propre paire de clés, distincte de
  // celles utilisées pour les paiements en ligne — voir payment-config.services.ts.
  terminalApiKey: string;
  terminalWebhookSecret: string;
  // Non secret (publicConfig) : identifiant du TPE par défaut pour les
  // paiements de terminal, ex. "PAX:3210211095" — utilisé plus tard côté
  // caisse pour savoir sur quel terminal envoyer un paiement.
  defaultTerminalId: string;
};

function emptyFormState(): FormState {
  return {
    enabled: false,
    displayName: '',
    shopId: '',
    apiKey: '',
    webhookSecret: '',
    terminalApiKey: '',
    terminalWebhookSecret: '',
    defaultTerminalId: '',
  };
}

const forms = reactive<Record<PaymentProcessor, FormState>>({
  mock: emptyFormState(),
  market_pay: emptyFormState(),
  on_site: emptyFormState(),
  payfast: emptyFormState(),
});

watch(
  configs,
  (val) => {
    if (!val) return;
    for (const cfg of val) {
      const f = forms[cfg.processor as PaymentProcessor];
      f.enabled = cfg.enabled;
      f.displayName = cfg.displayName ?? '';
      f.shopId = (cfg.publicConfig?.shopId as string) ?? '';
      f.defaultTerminalId =
        (cfg.publicConfig?.defaultTerminalId as string) ?? '';
    }
  },
  { immediate: true },
);

const saved = ref<PaymentProcessor | null>(null);
const saveError = ref<string | null>(null);
const attemptedSave = ref(false);

const activeConfig = computed(() =>
  configs.value?.find((c) => c.processor === selectedProcessor.value),
);
const hasAnySecret = computed(
  () =>
    !!activeConfig.value?.secretPreviews?.apiKey ||
    !!activeConfig.value?.secretPreviews?.webhookSecret ||
    !!activeConfig.value?.secretPreviews?.terminalApiKey ||
    !!activeConfig.value?.secretPreviews?.terminalWebhookSecret,
);

// Trois états, mutuellement exclusifs (voir spec) :
// - readonly : tous les champs secrets sont verrouillés (aperçu masqué).
// - terminal-edit : seuls defaultTerminalId/terminalApiKey/
//   terminalWebhookSecret sont éditables ; apiKey/webhookSecret restent
//   verrouillées et ne sont PAS renvoyées à la sauvegarde.
// - revoke-keys : les 5 champs (terminal + apiKey/webhookSecret) sont
//   éditables ; la sauvegarde envoie tout secretConfig.
type TEditMode = 'readonly' | 'terminal-edit' | 'revoke-keys';
const editMode = ref<TEditMode>('readonly');
const showTerminalSaveModal = ref(false);

function startTerminalEdit() {
  editMode.value = 'terminal-edit';
}
function startRevokeKeys() {
  showTerminalSaveModal.value = false;
  editMode.value = 'revoke-keys';
  forms[selectedProcessor.value].apiKey = '';
  forms[selectedProcessor.value].webhookSecret = '';
}
function cancelEdit() {
  editMode.value = 'readonly';
  attemptedSave.value = false;
  showTerminalSaveModal.value = false;
  const f = forms[selectedProcessor.value];
  f.apiKey = '';
  f.webhookSecret = '';
  f.terminalApiKey = '';
  f.terminalWebhookSecret = '';
  f.defaultTerminalId =
    (activeConfig.value?.publicConfig?.defaultTerminalId as string) ?? '';
}

const { mutate: doSave, isPending: saving } = useMutation({
  mutationFn: () => {
    const proc = selectedProcessor.value;
    const f = forms[proc];
    const publicConfig: Record<string, unknown> = {};
    if (proc === 'market_pay') publicConfig.shopId = f.shopId;
    if (proc === 'payfast' && f.defaultTerminalId)
      publicConfig.defaultTerminalId = f.defaultTerminalId;

    // terminal-edit : on ne touche PAS à secretConfig — apiKey/webhookSecret
    // ne sont ni lues ni renvoyées, seuls displayName/enabled/
    // defaultTerminalId(+terminal secrets, via un champ séparé plus bas)
    // sont concernés. revoke-keys : les 4 secrets sont envoyés ensemble
    // (contrat serveur — un champ omis garde sa valeur, secretConfig fourni
    // remplace tout l'objet, voir upsert.dto). Première configuration (aucun
    // secret en base) : les champs restent affichés en editMode 'readonly'
    // (voir isSecretLocked, verrouillé seulement si hasAnySecret) — sans ce
    // cas, une saisie initiale d'apiKey n'était jamais envoyée.
    let secretConfig: Record<string, unknown> | undefined;
    if (
      proc === 'payfast' &&
      (editMode.value === 'revoke-keys' || isFirstTimePayfastSetup.value)
    ) {
      secretConfig = { apiKey: f.apiKey };
      if (f.webhookSecret) secretConfig.webhookSecret = f.webhookSecret;
      if (f.terminalApiKey) secretConfig.terminalApiKey = f.terminalApiKey;
      if (f.terminalWebhookSecret)
        secretConfig.terminalWebhookSecret = f.terminalWebhookSecret;
    } else if (
      proc === 'payfast' &&
      editMode.value === 'terminal-edit' &&
      (f.terminalApiKey || f.terminalWebhookSecret)
    ) {
      // Terminal seul modifié : envoyer terminalApiKey/terminalWebhookSecret
      // par eux-mêmes (sans apiKey/webhookSecret) — voir doc §9, le serveur
      // doit préserver apiKey/webhookSecret déjà en base pour les clés
      // absentes de ce payload partiel.
      secretConfig = {};
      if (f.terminalApiKey) secretConfig.terminalApiKey = f.terminalApiKey;
      if (f.terminalWebhookSecret)
        secretConfig.terminalWebhookSecret = f.terminalWebhookSecret;
    }

    return paymentConfigService.upsert(props.storeId, proc, {
      enabled: f.enabled,
      displayName: f.displayName,
      publicConfig,
      ...(secretConfig !== undefined ? { secretConfig } : {}),
    });
  },
  onSuccess: () => {
    saved.value = selectedProcessor.value;
    saveError.value = null;
    attemptedSave.value = false;
    editMode.value = 'readonly';
    qc.invalidateQueries({ queryKey: ['payment-configs', props.storeId] });
    setTimeout(() => {
      saved.value = null;
    }, 2500);
  },
  onError: (e: any) => {
    saveError.value = e?.response?.data?.message ?? t('payment.saveError');
  },
});

// En mode revoke-keys, les deux clés principales sont obligatoires
// (webhookSecret est optionnel dans les autres modes). Première
// configuration (aucun secret en base) : seulement si le provider est activé
// et qu'une saisie a commencé — sinon un simple "Enregistrer" sur un
// formulaire vierge (provider désactivé) ne doit pas bloquer.
const isFirstTimePayfastSetup = computed(
  () =>
    selectedProcessor.value === 'payfast' &&
    !hasAnySecret.value &&
    (forms.payfast.enabled || !!forms.payfast.apiKey.trim()),
);
const showApiKeyError = computed(
  () =>
    attemptedSave.value &&
    (editMode.value === 'revoke-keys' || isFirstTimePayfastSetup.value) &&
    !forms[selectedProcessor.value].apiKey.trim(),
);
const showWebhookSecretError = computed(
  () =>
    attemptedSave.value &&
    (editMode.value === 'revoke-keys' || isFirstTimePayfastSetup.value) &&
    !forms[selectedProcessor.value].webhookSecret.trim(),
);

function save() {
  attemptedSave.value = true;
  if (
    (editMode.value === 'revoke-keys' || isFirstTimePayfastSetup.value) &&
    (!forms[selectedProcessor.value].apiKey.trim() ||
      !forms[selectedProcessor.value].webhookSecret.trim())
  ) {
    return;
  }
  if (editMode.value === 'terminal-edit' && hasAnySecret.value) {
    showTerminalSaveModal.value = true;
    return;
  }
  doSave();
}

function confirmTerminalSaveKeepingApiKeys() {
  showTerminalSaveModal.value = false;
  doSave();
}

type TSecretField =
  'apiKey' | 'webhookSecret' | 'terminalApiKey' | 'terminalWebhookSecret';
function isSecretLocked(field: TSecretField) {
  if (!hasAnySecret.value) return false;
  if (editMode.value === 'revoke-keys') return false;
  if (editMode.value === 'terminal-edit') {
    return field === 'apiKey' || field === 'webhookSecret';
  }
  return true;
}

watch(selectedProcessor, () => {
  editMode.value = 'readonly';
  showTerminalSaveModal.value = false;
  attemptedSave.value = false;
});

const curlExample = computed(() => {
  const proc = selectedProcessor.value;
  const f = forms[proc];
  const sid = props.storeId || '{storeId}';
  const base = 'http://localhost:8081';
  const body: Record<string, unknown> = {
    enabled: f.enabled,
    displayName: f.displayName || '',
  };
  if (proc === 'market_pay')
    body.publicConfig = { shopId: f.shopId || 'SHOP-1234' };
  if (proc === 'payfast') {
    const sec: Record<string, string> = {
      apiKey: f.apiKey || 'pf_test_51H...',
    };
    if (f.webhookSecret) sec.webhookSecret = f.webhookSecret;
    if (f.terminalApiKey) sec.terminalApiKey = f.terminalApiKey;
    if (f.terminalWebhookSecret)
      sec.terminalWebhookSecret = f.terminalWebhookSecret;
    body.secretConfig = sec;
    if (f.defaultTerminalId)
      body.publicConfig = { defaultTerminalId: f.defaultTerminalId };
  }
  return [
    `curl ${base}/api/payment-configs/${sid}/${proc} \\`,
    `  --request PUT \\`,
    `  --header 'x-client-app: admin-back-office' \\`,
    `  --header 'Content-Type: application/json' \\`,
    `  --data '${JSON.stringify(body, null, 2)}'`,
  ].join('\n');
});
</script>

<template>
  <div class="pcp">
    <div class="pcp__layout">
      <!-- Processors -->
      <div class="pcp__processors">
        <p class="pcp__section-label">Provider</p>
        <div class="pcp__proc-list">
          <button
            v-for="p in PROCESSORS"
            :key="p.key"
            class="pcp__proc-btn"
            :class="{ 'pcp__proc-btn--active': selectedProcessor === p.key }"
            @click="selectedProcessor = p.key"
          >
            <div class="pcp__proc-icon">
              <i :class="`pi ${p.icon}`" />
            </div>
            <div class="pcp__proc-info">
              <span class="pcp__proc-name">{{ p.label }}</span>
              <span
                v-if="configs?.find((c) => c.processor === p.key)"
                class="pcp__proc-chip"
                :class="
                  configs?.find((c) => c.processor === p.key)?.enabled
                    ? 'pcp__proc-chip--on'
                    : 'pcp__proc-chip--off'
                "
              >
                {{
                  configs?.find((c) => c.processor === p.key)?.enabled
                    ? t('common.active')
                    : t('payment.configured')
                }}
              </span>
            </div>
            <i class="pi pi-chevron-right pcp__proc-chevron" />
          </button>
        </div>
      </div>

      <!-- Formulaire -->
      <div class="pcp__form-col">
        <div v-if="loading" class="pcp__skeleton">
          <div class="pcp__skel-line" style="width: 40%; height: 1.25rem" />
          <div class="pcp__skel-line" style="width: 70%; height: 0.875rem" />
          <div
            class="pcp__skel-line"
            style="width: 100%; height: 2.5rem; margin-top: 1rem"
          />
        </div>

        <template v-else>
          <div class="pcp__form-card">
            <div class="pcp__form-head">
              <div class="pcp__form-icon">
                <i :class="`pi ${activeProc.icon}`" />
              </div>
              <div>
                <h2 class="pcp__form-title">{{ activeProc.label }}</h2>
                <p class="pcp__form-desc">{{ activeProc.desc }}</p>
              </div>
            </div>

            <div class="pcp__toggle-row">
              <div>
                <p class="pcp__toggle-label">{{ t('payment.toggleLabel') }}</p>
                <p class="pcp__toggle-sub">{{ t('payment.toggleSub') }}</p>
              </div>
              <button
                class="pcp__toggle"
                :class="{ 'pcp__toggle--on': forms[selectedProcessor].enabled }"
                @click="
                  forms[selectedProcessor].enabled =
                    !forms[selectedProcessor].enabled
                "
              >
                <span class="pcp__toggle-thumb" />
              </button>
            </div>

            <div class="pcp__field">
              <label class="pcp__label">{{
                t('payment.displayNameLabel')
              }}</label>
              <input
                v-model="forms[selectedProcessor].displayName"
                type="text"
                class="pcp__input"
                :placeholder="t('payment.displayNamePlaceholder')"
              />
            </div>

            <template v-if="selectedProcessor === 'market_pay'">
              <div class="pcp__field">
                <label class="pcp__label"
                  >Shop ID <span class="pcp__label-required">*</span></label
                >
                <input
                  v-model="forms.market_pay.shopId"
                  type="text"
                  class="pcp__input"
                  placeholder="SHOP-1234"
                />
                <p class="pcp__hint">{{ t('payment.shopIdHint') }}</p>
              </div>
            </template>

            <template v-if="selectedProcessor === 'payfast'">
              <div class="pcp__terminal-sep">
                <i class="pi pi-tablet" />
                <span>{{ t('payment.terminalSectionTitle') }}</span>
                <button
                  v-if="editMode === 'readonly' && hasAnySecret"
                  type="button"
                  class="pcp__edit-terminal-btn"
                  @click="startTerminalEdit()"
                >
                  <i class="pi pi-pencil" /> {{ t('payment.editTerminal') }}
                </button>
              </div>
              <p class="pcp__hint pcp__terminal-hint">
                {{ t('payment.terminalSectionHint') }}
              </p>

              <div class="pcp__field">
                <label class="pcp__label"
                  >Default Terminal ID
                  <span class="pcp__label-optional"
                    >({{ t('common.optional') }})</span
                  ></label
                >
                <input
                  v-model="forms.payfast.defaultTerminalId"
                  type="text"
                  class="pcp__input"
                  :disabled="editMode === 'readonly' && hasAnySecret"
                  placeholder="PAX:3210211095"
                />
                <p class="pcp__hint">
                  {{ t('payment.defaultTerminalIdHint') }}
                </p>
              </div>

              <div class="pcp__field">
                <label class="pcp__label"
                  >Terminal API Key
                  <span class="pcp__label-optional"
                    >({{ t('common.optional') }})</span
                  ></label
                >
                <div class="pcp__secret-wrap">
                  <i class="pi pi-lock pcp__secret-icon" />
                  <input
                    v-if="isSecretLocked('terminalApiKey')"
                    :value="activeConfig?.secretPreviews?.terminalApiKey"
                    type="text"
                    readonly
                    class="pcp__input pcp__input--secret pcp__input--locked"
                  />
                  <input
                    v-else
                    v-model="forms.payfast.terminalApiKey"
                    type="password"
                    class="pcp__input pcp__input--secret"
                    placeholder="pf_term_51H..."
                  />
                </div>
                <p class="pcp__hint">{{ t('payment.terminalApiKeyHint') }}</p>
              </div>
              <div class="pcp__field">
                <label class="pcp__label"
                  >Terminal Webhook Secret
                  <span class="pcp__label-optional"
                    >({{ t('common.optional') }})</span
                  ></label
                >
                <div class="pcp__secret-wrap">
                  <i class="pi pi-lock pcp__secret-icon" />
                  <input
                    v-if="isSecretLocked('terminalWebhookSecret')"
                    :value="activeConfig?.secretPreviews?.terminalWebhookSecret"
                    type="text"
                    readonly
                    class="pcp__input pcp__input--secret pcp__input--locked"
                  />
                  <input
                    v-else
                    v-model="forms.payfast.terminalWebhookSecret"
                    type="password"
                    class="pcp__input pcp__input--secret"
                    placeholder="whsec_term_..."
                  />
                </div>
                <p class="pcp__hint">
                  {{ t('payment.terminalWebhookSecretHint') }}
                </p>
              </div>

              <div class="pcp__terminal-sep">
                <i class="pi pi-key" />
                <span>{{ t('payment.mainKeysSectionTitle') }}</span>
                <button
                  v-if="editMode === 'readonly' && isSecretLocked('apiKey')"
                  type="button"
                  class="pcp__revoke-btn pcp__revoke-btn--inline"
                  @click="startRevokeKeys()"
                >
                  <i class="pi pi-key" /> {{ t('payment.revokeKeys') }}
                </button>
              </div>

              <div class="pcp__field">
                <label class="pcp__label"
                  >API Key <span class="pcp__label-required">*</span></label
                >
                <div class="pcp__secret-wrap">
                  <i class="pi pi-lock pcp__secret-icon" />
                  <input
                    v-if="isSecretLocked('apiKey')"
                    :value="activeConfig?.secretPreviews?.apiKey"
                    type="text"
                    readonly
                    class="pcp__input pcp__input--secret pcp__input--locked"
                  />
                  <input
                    v-else
                    v-model="forms.payfast.apiKey"
                    type="password"
                    class="pcp__input pcp__input--secret"
                    :class="{ 'pcp__input--error': showApiKeyError }"
                    placeholder="pf_test_51H..."
                  />
                </div>
                <p v-if="showApiKeyError" class="pcp__field-error">
                  {{ t('product.requiredField') }}
                </p>
                <p v-else class="pcp__hint">{{ t('payment.apiKeyHint') }}</p>
              </div>
              <div class="pcp__field">
                <label class="pcp__label"
                  >Webhook Secret
                  <span
                    v-if="editMode !== 'revoke-keys'"
                    class="pcp__label-optional"
                    >({{ t('common.optional') }})</span
                  >
                  <span v-else class="pcp__label-required">*</span></label
                >
                <div class="pcp__secret-wrap">
                  <i class="pi pi-lock pcp__secret-icon" />
                  <input
                    v-if="isSecretLocked('webhookSecret')"
                    :value="activeConfig?.secretPreviews?.webhookSecret"
                    type="text"
                    readonly
                    class="pcp__input pcp__input--secret pcp__input--locked"
                  />
                  <input
                    v-else
                    v-model="forms.payfast.webhookSecret"
                    type="password"
                    class="pcp__input pcp__input--secret"
                    :class="{ 'pcp__input--error': showWebhookSecretError }"
                    placeholder="whsec_..."
                  />
                </div>
                <p v-if="showWebhookSecretError" class="pcp__field-error">
                  {{ t('product.requiredField') }}
                </p>
                <p v-else class="pcp__hint">{{ t('payment.webhookHint') }}</p>
              </div>
            </template>

            <template
              v-if="
                selectedProcessor === 'mock' || selectedProcessor === 'on_site'
              "
            >
              <div class="pcp__info-box">
                <i class="pi pi-info-circle" />
                <span v-if="selectedProcessor === 'mock'">{{
                  t('payment.infoMock')
                }}</span>
                <span v-else>{{ t('payment.infoOnSite') }}</span>
              </div>
            </template>

            <div v-if="saveError" class="pcp__error">
              <i class="pi pi-exclamation-triangle" />
              {{ saveError }}
            </div>

            <div class="pcp__form-actions">
              <div v-if="activeConfig" class="pcp__store-status">
                <span
                  class="pcp__status-dot"
                  :class="
                    activeConfig.enabled
                      ? 'pcp__status-dot--on'
                      : 'pcp__status-dot--off'
                  "
                />
                {{
                  activeConfig.enabled
                    ? t('common.active')
                    : t('common.inactive')
                }}
                {{ t('payment.onThisStore') }}
              </div>
              <template
                v-if="
                  selectedProcessor === 'payfast' && editMode !== 'readonly'
                "
              >
                <button
                  type="button"
                  class="pcp__save-btn pcp__save-btn--ghost"
                  :disabled="saving"
                  @click="cancelEdit()"
                >
                  <i class="pi pi-times" /> {{ t('product.cancel') }}
                </button>
                <button
                  class="pcp__save-btn"
                  :class="{
                    'pcp__save-btn--saved': saved === selectedProcessor,
                  }"
                  :disabled="saving"
                  @click="save()"
                >
                  <i
                    :class="
                      saved === selectedProcessor
                        ? 'pi pi-check'
                        : saving
                          ? 'pi pi-spin pi-spinner'
                          : 'pi pi-save'
                    "
                  />
                  {{
                    saved === selectedProcessor
                      ? t('payment.saved')
                      : t('payment.save')
                  }}
                </button>
              </template>
              <button
                v-else
                class="pcp__save-btn"
                :class="{ 'pcp__save-btn--saved': saved === selectedProcessor }"
                :disabled="saving"
                @click="save()"
              >
                <i
                  :class="
                    saved === selectedProcessor
                      ? 'pi pi-check'
                      : saving
                        ? 'pi pi-spin pi-spinner'
                        : 'pi pi-save'
                  "
                />
                {{
                  saved === selectedProcessor
                    ? t('payment.saved')
                    : t('payment.save')
                }}
              </button>
            </div>
          </div>

          <!-- Modale : Save après édition terminal seul — conserver ou révoquer les clés API -->
          <Teleport to="body">
            <div
              v-if="showTerminalSaveModal"
              class="pcp__modal-backdrop"
              @click.self="showTerminalSaveModal = false"
            >
              <div class="pcp__modal">
                <div class="pcp__modal-ico"><i class="pi pi-key" /></div>
                <h2 class="pcp__modal-title">
                  {{ t('payment.terminalSaveWarningTitle') }}
                </h2>
                <p class="pcp__modal-text">
                  {{ t('payment.terminalSaveWarningBody') }}
                </p>
                <div class="pcp__modal-actions">
                  <button
                    class="pcp__save-btn pcp__save-btn--ghost"
                    @click="startRevokeKeys()"
                  >
                    {{ t('payment.revokeKeys') }}
                  </button>
                  <button
                    class="pcp__save-btn"
                    @click="confirmTerminalSaveKeepingApiKeys()"
                  >
                    {{ t('payment.terminalSaveWarningContinue') }}
                  </button>
                </div>
              </div>
            </div>
          </Teleport>

          <div class="pcp__curl-card">
            <div class="pcp__curl-head">
              <i class="pi pi-code" />
              <span>{{ t('payment.curlExample') }}</span>
            </div>
            <pre class="pcp__curl-code">{{ curlExample }}</pre>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pcp {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Layout providers + form */
.pcp__layout {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.25rem;
  align-items: start;
}
@media (max-width: 768px) {
  .pcp__layout {
    grid-template-columns: 1fr;
  }
}

.pcp__processors {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.pcp__section-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-muted, #999);
  margin-bottom: 0.25rem;
}
.pcp__proc-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.pcp__proc-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-radius: 1rem;
  border: 1.5px solid transparent;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: all 0.15s ease;
}
.pcp__proc-btn:hover {
  background: rgba(255, 255, 255, 0.9);
}
.pcp__proc-btn--active {
  background: rgba(255, 255, 255, 0.85);
  border-color: var(--nxt-primary, #141c29);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.pcp__proc-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  background: var(--nxt-surface-high, #e7e8e9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
  transition: background 0.15s;
}
.pcp__proc-btn--active .pcp__proc-icon {
  background: var(--nxt-primary, #141c29);
  color: #fff;
}
.pcp__proc-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.pcp__proc-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pcp__proc-chip {
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.1rem 0.45rem;
  border-radius: 9999px;
  width: fit-content;
}
.pcp__proc-chip--on {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}
.pcp__proc-chip--off {
  background: var(--nxt-surface-high);
  color: var(--nxt-on-surface-variant);
}
.pcp__proc-chevron {
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted, #999);
}

/* Form */
.pcp__form-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.pcp__form-card {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.pcp__form-head {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}
.pcp__form-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: var(--nxt-primary, #141c29);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.125rem;
  flex-shrink: 0;
}
.pcp__form-title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.pcp__form-desc {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}

.pcp__toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.125rem;
  background: var(--nxt-surface-low, #f8f9fa);
  border-radius: 0.875rem;
}
.pcp__toggle-label {
  margin: 0 0 0.2rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pcp__toggle-sub {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.pcp__toggle {
  width: 2.75rem;
  height: 1.625rem;
  border-radius: 9999px;
  border: none;
  background: var(--nxt-surface-highest, #e1e3e4);
  cursor: pointer;
  position: relative;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.25s;
}
.pcp__toggle--on {
  background: var(--nxt-primary, #141c29);
}
.pcp__toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.25s;
  display: block;
}
.pcp__toggle--on .pcp__toggle-thumb {
  transform: translateX(1.125rem);
}

.pcp__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}
.pcp__label-required {
  color: var(--nxt-error, #ba1a1a);
}
.pcp__label-optional {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.75rem;
}
.pcp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.pcp__input {
  height: 2.75rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  padding: 0 0.875rem;
  outline: none;
  transition: border-color 0.15s;
}
.pcp__input:focus {
  border-color: var(--nxt-primary, #141c29);
}
.pcp__input--error {
  border-color: var(--nxt-error, #ba1a1a);
}
.pcp__field-error {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-error, #ba1a1a);
  font-weight: 600;
}
.pcp__secret-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.pcp__secret-icon {
  position: absolute;
  left: 0.875rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.pcp__input--secret {
  padding-left: 2.5rem;
  font-family: monospace;
}
.pcp__input--locked {
  padding-right: 0.875rem;
  color: #16a34a;
  cursor: default;
  background: rgba(22, 163, 74, 0.05);
  border-color: rgba(22, 163, 74, 0.25);
}
.pcp__edit-terminal-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
  height: 1.75rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-low, #f8f9fa);
  color: var(--nxt-on-surface);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  text-transform: none;
  letter-spacing: normal;
  transition: background 0.15s;
}
.pcp__edit-terminal-btn:hover {
  background: var(--nxt-surface-high, #e7e8e9);
}
.pcp__revoke-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  align-self: flex-start;
  height: 2.125rem;
  padding: 0 0.875rem;
  margin-top: 0.125rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(220, 38, 38, 0.35);
  background: rgba(220, 38, 38, 0.06);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.pcp__revoke-btn:hover {
  background: rgba(220, 38, 38, 0.14);
}
[data-theme='dark'] .pcp__revoke-btn {
  border-color: rgba(220, 38, 38, 0.5);
  background: rgba(220, 38, 38, 0.12);
}
[data-theme='dark'] .pcp__revoke-btn:hover {
  background: rgba(220, 38, 38, 0.22);
}
.pcp__revoke-btn--inline {
  margin-left: auto;
  margin-top: 0;
  height: 1.75rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  text-transform: none;
  letter-spacing: normal;
}
/* Modale confirmation révocation */
.pcp__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.pcp__modal {
  background: var(--nxt-card-bg, #fff);
  border-radius: 1rem;
  padding: 1.75rem;
  max-width: 26rem;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
[data-theme='dark'] .pcp__modal {
  background: #1e2025;
}
.pcp__modal-ico {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}
.pcp__modal-title {
  margin: 0 0 0.5rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.pcp__modal-text {
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant, #666);
  line-height: 1.5;
}
.pcp__modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
}
.pcp__hint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #999);
  line-height: 1.4;
}

.pcp__terminal-sep {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}
.pcp__terminal-hint {
  margin-top: -0.5rem;
}

.pcp__info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem 1rem;
  background: rgba(240, 243, 255, 0.6);
  border: 1px solid rgba(20, 28, 41, 0.08);
  border-radius: 0.875rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.pcp__info-box .pi {
  font-size: 0.875rem;
  color: var(--nxt-primary, #141c29);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.pcp__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: rgba(186, 26, 26, 0.06);
  border: 1px solid rgba(186, 26, 26, 0.15);
  color: var(--nxt-error, #ba1a1a);
  font-size: 0.8125rem;
  font-weight: 600;
}

.pcp__form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
}
.pcp__store-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  flex: 1;
}
.pcp__status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}
.pcp__status-dot--on {
  background: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15);
}
.pcp__status-dot--off {
  background: #94a3b8;
}

.pcp__save-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.375rem;
  border-radius: 0.75rem;
  border: none;
  background: var(--nxt-primary, #141c29);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(20, 28, 41, 0.2);
  transition:
    opacity 0.15s,
    background 0.2s;
}
.pcp__save-btn:hover:not(:disabled) {
  opacity: 0.88;
}
.pcp__save-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.pcp__save-btn--saved {
  background: #16a34a !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3) !important;
}
.pcp__save-btn--ghost {
  background: transparent;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  color: var(--nxt-on-surface);
  box-shadow: none;
}
.pcp__save-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
  opacity: 1;
}
[data-theme='dark'] .pcp__save-btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}
.pcp__save-btn--danger {
  background: #dc2626;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25);
}
.pcp__save-btn--danger:hover:not(:disabled) {
  opacity: 0.9;
}

.pcp__curl-card {
  background: rgba(15, 17, 22, 0.96);
  border-radius: 1.25rem;
  overflow: hidden;
}
.pcp__curl-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.8125rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
}
.pcp__curl-code {
  margin: 0;
  padding: 1.125rem 1.25rem;
  font-size: 0.75rem;
  line-height: 1.7;
  color: #93c5fd;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;
}

.pcp__skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
}
.pcp__skel-line {
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.06);
  animation: skel-pulse 1.4s ease-in-out infinite;
}
@keyframes skel-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
</style>

<style>
[data-theme='dark'] .pcp__form-card {
  background: rgba(30, 32, 36, 0.75);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .pcp__proc-btn {
  background: rgba(30, 32, 36, 0.6);
}
[data-theme='dark'] .pcp__proc-btn:hover {
  background: rgba(40, 43, 48, 0.9);
}
[data-theme='dark'] .pcp__proc-btn--active {
  background: rgba(40, 43, 48, 0.9);
  border-color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .pcp__input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
}
[data-theme='dark'] .pcp__toggle-row {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .pcp__info-box {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
}
</style>
